import {useEffect, useRef, useState} from "react";
import "./Partners.css";
import Partner from "./Partner/Partner.jsx";

export default function Partners() {
    const partners = [
        {image: "/images/partners/oh-ochi-partner.webp", name: "Oh OCHI Brand"},
        {image: "/images/partners/sculptor-partner.webp", name: "SCULPTOR"},
        {image: "/images/partners/staleks-partner.webp", name: "Staleks"},
        {image: "/images/partners/lash-academy-partner.webp", name: "Lash Academy"},
        {image: "/images/partners/lash-tile-partner.webp", name: "Lash Tile Global"},
        {image: "/images/partners/pimp-my-lashes-partner.webp", name: "Pimp My Lashes"}
    ];

    const containerRef = useRef(null);

    // desktop drag state (touch uses native scrolling)
    const isDraggingRef = useRef(false);
    const startXRef = useRef(0);
    const scrollLeftRef = useRef(0);
    const rafIdRef = useRef(0);

    const [draggingClass, setDraggingClass] = useState(false);
    const [hasOverflow, setHasOverflow] = useState(false);

    // Measure overflow (on mount & resize)
    useEffect(() => {
        const el = containerRef.current;
        if (!el) return;

        const measure = () => {
            const overflow = el.scrollWidth > el.clientWidth + 1;
            setHasOverflow(overflow);
            if (overflow) el.scrollLeft = Math.min(el.scrollLeft, el.scrollWidth - el.clientWidth);
        };

        measure();

        const ro = new ResizeObserver(measure);
        ro.observe(el);
        const onResize = () => measure();
        window.addEventListener("resize", onResize, {passive: true});

        // Re-measure after images load
        const imgs = Array.from(el.querySelectorAll("img"));
        let pending = imgs.filter((img) => !img.complete).length;
        const onDone = () => {
            if (pending > 0 && --pending === 0) measure();
        };
        imgs.forEach((img) => {
            if (img.complete) return;
            img.addEventListener("load", onDone, {once: true, passive: true});
            img.addEventListener("error", onDone, {once: true, passive: true});
        });

        return () => {
            ro.disconnect();
            window.removeEventListener("resize", onResize);
        };
    }, []);

    // Only enable JS drag for mouse/pen. Touch = native scroll.
    const isPointerDragEligible = (e) => e.pointerType === "mouse" || e.pointerType === "pen";

    const onPointerDown = (e) => {
        if (!hasOverflow || !isPointerDragEligible(e)) return; // let touch devices scroll natively

        const el = containerRef.current;
        if (!el) return;

        el.setPointerCapture?.(e.pointerId);
        isDraggingRef.current = true;
        setDraggingClass(true);

        const rect = el.getBoundingClientRect();
        startXRef.current = e.clientX - rect.left;
        scrollLeftRef.current = el.scrollLeft;
    };

    const onPointerMove = (e) => {
        if (!hasOverflow || !isPointerDragEligible(e)) return;
        if (!isDraggingRef.current) return;

        const el = containerRef.current;
        if (!el) return;

        // DO NOT call e.preventDefault() – that would block native touch scrolling.
        if (rafIdRef.current) return;
        const rect = el.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const walk = (x - startXRef.current) * 2; // speed factor

        rafIdRef.current = requestAnimationFrame(() => {
            el.scrollLeft = scrollLeftRef.current - walk;
            rafIdRef.current = 0;
        });
    };

    const endDrag = (e) => {
        if (!isDraggingRef.current) return;
        isDraggingRef.current = false;
        setDraggingClass(false);

        if (rafIdRef.current) {
            cancelAnimationFrame(rafIdRef.current);
            rafIdRef.current = 0;
        }

        const el = containerRef.current;
        if (el && e?.pointerId != null) el.releasePointerCapture?.(e.pointerId);
    };

    return (
        <div id="partners">
            <span id="partners-label-1">Nasi partnerzy:</span>

            <div
                id="partners-container"
                ref={containerRef}
                className={[
                    draggingClass ? "dragging" : "",
                    hasOverflow ? "is-scrollable" : "no-overflow",
                    "scrollbar-hidden",
                ].join(" ")}
                // Desktop/pen drag handlers
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={endDrag}
                onPointerCancel={endDrag}
                onPointerLeave={endDrag}
                role="list"
                aria-label="Lista partnerów"
            >
                {hasOverflow ? (
                    partners.map((partner, index) => (
                        <Partner partner={partner} index={index} key={index}/>
                    ))
                ) : (
                    <div className="partners-inner">
                        {partners.map((partner, index) => (
                            <Partner partner={partner} index={index} key={index}/>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
